import { ref } from 'vue'
import { defineStore } from 'pinia'
import { createConsumer, Consumer, Subscription } from '@rails/actioncable'
import type { OwnerInboxCounts } from '../types/owner-inbox'

export const REFRESH_DEBOUNCE_MS = 1000
export const POLL_INTERVAL_MS = 300_000

// Живые счётчики очередей владельца. Первое значение приезжает общим пропом
// owner_inbox (seed), дальше сервер шлёт в OwnerInboxChannel пустой сигнал
// «очереди изменились», а числа берём заново из GET /owner/inbox. Время само
// очереди двигает без сигнала (брошенные платежи, окно 24 часа), их ловят
// опрос раз в 5 минут при видимой вкладке и обновление при возврате на вкладку.
export const useOwnerInboxStore = defineStore('ownerInbox', () => {
  const counts = ref<OwnerInboxCounts | undefined>(undefined)

  let consumer: Consumer | null = null
  let subscription: Subscription | null = null
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  function seed(value: OwnerInboxCounts | undefined): void {
    counts.value = value
  }

  async function refresh(): Promise<void> {
    try {
      const response = await fetch('/owner/inbox', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      counts.value = (await response.json()) as OwnerInboxCounts
    } catch {
      // сеть отвалилась: оставляем прежние числа
    }
  }

  function scheduleRefresh(): void {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void refresh()
    }, REFRESH_DEBOUNCE_MS)
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') scheduleRefresh()
  }

  function signal(): void {
    if (subscription) scheduleRefresh()
  }

  function start(): void {
    if (subscription) return
    consumer ??= createConsumer()
    subscription = consumer.subscriptions.create(
      { channel: 'OwnerInboxChannel' },
      { received: signal, connected: signal },
    )
    document.addEventListener('visibilitychange', onVisibilityChange)
    pollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') scheduleRefresh()
    }, POLL_INTERVAL_MS)
  }

  function stop(): void {
    subscription?.unsubscribe()
    subscription = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = null
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null
  }

  return { counts, seed, start, stop, scheduleRefresh, refresh }
})
